import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  PaginatedAdoptionReports,
  AdoptionReportSubmissionData,
  AdoptionReportApiResult,
} from '../types/adoptionReports';
import api from '../services/api';
import { adoptionReportsAtom } from '../atoms/adoptionReportAtom';
import { calculateTotalAdoptions } from '../utils/timeframeHelpers';

let pendingAdoptionReportsRequest: Promise<PaginatedAdoptionReports> | null = null;
let pendingAdoptionReportsRequestKey: string | null = null;

/**
 * Custom hook to read the adoption reports state.
 * Returns the current reports or null if not loaded/error.
 */
export const useAdoptionReportState = () => {
  const adoptionReportsData = useAtomValue(adoptionReportsAtom);
  return adoptionReportsData;
};

/**
 * Custom hook to provide actions related to adoption reports.
 * Provides functions to submit adoption reports from the backend API.
 */
export const useAdoptionReportActions = () => {
  const setAdoptionReports = useSetAtom(adoptionReportsAtom);

  // Function to trigger a fetch of adoption reports from the backend
  const getAdoptionReports = useCallback(
    async (filter?: string, page = 1, limit = 10) => {
      setAdoptionReports(null); // Set to null while fetching
      let currentRequest: Promise<PaginatedAdoptionReports> | null = null;
      try {
        // Build query params for pagination and optional filter
        let url = `/adoption-reports?page=${page}&limit=${limit}`;
        if (filter && filter !== 'all') {
          url += `&filter=${filter}`;
        }

        const requestKey = `${filter || 'all'}|${page}|${limit}`;
        if (pendingAdoptionReportsRequest && pendingAdoptionReportsRequestKey === requestKey) {
          const cachedData = await pendingAdoptionReportsRequest;
          const transformedData = {
            ...cachedData,
            data: cachedData.data.map((report) => ({
              ...report,
              totalAdoptions: report.adoptionCounts
                ? calculateTotalAdoptions(report.adoptionCounts)
                : 0,
            })),
          };
          setAdoptionReports(transformedData);
          return;
        }

        pendingAdoptionReportsRequestKey = requestKey;
        currentRequest = (async () => {
          const response = await api.get<PaginatedAdoptionReports>(url);
          return response.data;
        })();
        pendingAdoptionReportsRequest = currentRequest;
        const responseData = await currentRequest;

        // Transform the data to calculate totalAdoptions for each report
        const transformedData = {
          ...responseData,
          data: responseData.data.map((report) => ({
            ...report,
            totalAdoptions: report.adoptionCounts
              ? calculateTotalAdoptions(report.adoptionCounts)
              : 0,
          })),
        };

        setAdoptionReports(transformedData);
      } catch (error) {
        console.error('Failed to fetch adoption reports:', error);
        // Keep data null on failure
        setAdoptionReports(null);
      } finally {
        if (currentRequest && pendingAdoptionReportsRequest === currentRequest) {
          pendingAdoptionReportsRequest = null;
          pendingAdoptionReportsRequestKey = null;
        }
      }
    },
    [setAdoptionReports]
  );

  // Function to submit adoption report
  const submitAdoptionReport = useCallback(
    async (submissionData: AdoptionReportSubmissionData): Promise<AdoptionReportApiResult> => {
      try {
        // Format the data for submission
        const requestPayload = {
          timeframe_start: submissionData.timeframe_start,
          adoptionCounts: submissionData.adoptionCounts,
          submissionDate: new Date().toISOString(),
        };

        await api.post('/adoption-reports', requestPayload);

        // Return success
        return { success: true };
      } catch (error) {
        console.error('Failed to submit adoption report:', error);
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Unknown error occurred'),
        };
      }
    },
    []
  );

  // Function to update adoption report
  const updateAdoptionReport = useCallback(
    async (
      timeframeStart: string,
      submissionData: AdoptionReportSubmissionData
    ): Promise<AdoptionReportApiResult> => {
      try {
        // Format the data for update
        const requestPayload = {
          timeframe_start: submissionData.timeframe_start,
          adoptionCounts: submissionData.adoptionCounts,
          submissionDate: new Date().toISOString(),
        };

        await api.put(`/adoption-reports/${timeframeStart}`, requestPayload);

        // Return success
        return { success: true };
      } catch (error) {
        console.error('Failed to update adoption report:', error);
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Unknown error occurred'),
        };
      }
    },
    []
  );

  // Function to submit zero adoption report (no adoptions for the period)
  const submitZeroAdoptionReport = useCallback(
    async (timeframeStart: string): Promise<AdoptionReportApiResult> => {
      const zeroData: AdoptionReportSubmissionData = {
        timeframe_start: timeframeStart,
        adoptionCounts: {
          adultSeniorDogs: 0,
          puppies: 0,
          adultSeniorCats: 0,
          kittens: 0,
        },
      };

      return submitAdoptionReport(zeroData);
    },
    [submitAdoptionReport]
  );

  return {
    getAdoptionReports,
    submitAdoptionReport,
    updateAdoptionReport,
    submitZeroAdoptionReport,
  };
};
